// ABOUTME: One source of truth for per-study-family navigation — label, base route and tool cards.
// ABOUTME: Components read it by family so adding a family is data-only, not another page clone.
import { StudyFamily } from './interfaces/i-datasets';

export interface ToolCard {
  icon: string;
  title: string;
  description: string;
  route: string;
  comingSoon?: boolean;
  beta?: boolean;
}

export interface StudyFamilyNav {
  /** Human label used in page titles and headings. */
  label: string;
  /** Route prefix owning this family's pages, e.g. '/gwas-16'. */
  baseRoute: string;
  /** Tools offered by this family, in display order. */
  tools: ToolCard[];
  /**
   * Study-specific caveat shown above the dataset list, e.g. a MAF cut-off. Omitted where no
   * published figure exists — an absent notice is correct, a borrowed one is not.
   */
  datasetsNotice?: string;
}

// Enroll-HD WGS+ (v3) is SNP-level only: it has no FUMA/gene-level output and no MAGMA reference
// set, so the gene-level and pathway tools are deliberately absent rather than shown-and-empty.
export const STUDY_FAMILY_NAV: Record<StudyFamily, StudyFamilyNav> = {
  gwas15: {
    label: 'GWAS 1-5',
    baseRoute: '/gwas-15',
    tools: [],
  },
  gwas16: {
    label: 'GWAS 1-6',
    baseRoute: '/gwas-16',
    datasetsNotice: 'SNPs with MAF ≥0.1% are included in GWAS1-6 summary data.',
    tools: [
      {
        icon: 'layers-outline',
        title: 'Gene-Level Multi-Endpoint',
        description:
          'Compare gene-level associations across multiple phenotypes simultaneously',
        route: '/gwas-16/gene-multi',
        beta: true,
      },
      {
        icon: 'file-text-outline',
        title: 'Single Dataset Explorer',
        description: 'Deep dive into SNP-level data for one phenotype at a time',
        route: '/gwas-16/datasets',
      },
      {
        icon: 'grid-outline',
        title: 'SNP-Level Multi-Endpoint',
        description:
          'Compare SNP-level associations across phenotypes in a targeted region',
        route: '/gwas-16/snp-multi',
        beta: true,
      },
      {
        icon: 'bulb-outline',
        title: 'Pathway Analysis',
        description: 'Gene set enrichment and pathway analysis tools',
        route: '/gwas-16/gsea',
        beta: true,
      },
    ],
  },
  v3: {
    label: 'Enroll-HD WGS+',
    baseRoute: '/enroll-hd',
    tools: [
      {
        icon: 'file-text-outline',
        title: 'Single Dataset Explorer',
        description: 'Deep dive into SNP-level data for one phenotype at a time',
        route: '/enroll-hd/datasets',
      },
      {
        icon: 'grid-outline',
        title: 'SNP-Level Multi-Endpoint',
        description:
          'Compare SNP-level associations across phenotypes in a targeted region',
        route: '/enroll-hd/snp-multi',
        beta: true,
      },
    ],
  },
};

/** Nav config for a family, defaulting to GWAS 1-6 (the historical behaviour of these pages). */
export function navFor(family: StudyFamily | undefined): StudyFamilyNav {
  return STUDY_FAMILY_NAV[family ?? 'gwas16'] ?? STUDY_FAMILY_NAV['gwas16'];
}
